import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';

// Dictate into a text box. The same recording plumbing the logger's composer
// uses (MediaRecorder → the server's transcription endpoint → text appended
// to whatever was typed), lifted into a hook so the Ask composer can offer a
// mic without owning 150 lines of recorder state.
//
// Prod's proxy rejects bodies over 1MB; the duration cap keeps a clip under it
// without trusting the encoder to honour a bitrate (measured: ~14.5KB/s opus).

const MAX_RECORDING_SECONDS = 60;
const MAX_UPLOAD_BYTES = 1000 * 1024;

const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || '';
};

export const canRecordHere = typeof window !== 'undefined'
  && typeof MediaRecorder !== 'undefined'
  && !!navigator.mediaDevices?.getUserMedia;

const useVoiceNote = (onText) => {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const cancelledRef = useRef(false);
  const stopRef = useRef(null);

  const releaseMic = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  // Never leave the mic hot if the panel closes mid-recording.
  useEffect(() => () => {
    cancelledRef.current = true;
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch (e) { /* already stopped */ }
    }
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  useEffect(() => {
    if (!recording) return undefined;
    setElapsed(0);
    const id = setInterval(() => setElapsed((s) => {
      const next = s + 1;
      if (next >= MAX_RECORDING_SECONDS) stopRef.current?.();
      return next;
    }), 1000);
    return () => clearInterval(id);
  }, [recording]);

  const transcribe = useCallback(async (blob, mimeType) => {
    setTranscribing(true);
    try {
      const form = new FormData();
      const ext = mimeType.includes('mp4') ? 'mp4' : mimeType.includes('ogg') ? 'ogg' : 'webm';
      form.append('audio', blob, `clip.${ext}`);
      // Drop the JSON content-type so the browser sets multipart with its boundary.
      const res = await apiClient.post('/api/health/transcribe', form, {
        timeout: 120000,
        headers: { 'Content-Type': undefined },
      });
      const text = (res.data?.text || '').trim();
      if (!text) {
        toast('I didn’t catch any words there — try again a bit closer to the mic.');
        return;
      }
      onText?.(text);
    } catch (error) {
      const status = error.response?.status;
      if (status === 429) toast.error('Slow down a moment — too many recordings.');
      else if (status === 413) toast.error('That recording was too long. Try a shorter one, or type it.');
      else toast.error('Couldn’t transcribe that. Please try again or type it.');
    } finally {
      setTranscribing(false);
    }
  }, [onText]);

  const start = useCallback(async () => {
    if (!canRecordHere || recording || transcribing) return;
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      toast.error(e.name === 'NotAllowedError' || e.name === 'SecurityError'
        ? 'Microphone access denied. Please allow microphone permissions.'
        : 'Couldn’t reach your microphone. You can type instead.');
      return;
    }
    cancelledRef.current = false;
    streamRef.current = stream;
    chunksRef.current = [];
    const mimeType = pickMimeType();
    let recorder;
    try {
      recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    } catch (e) {
      releaseMic();
      toast.error('Recording isn’t supported in this browser. Please type instead.');
      return;
    }
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onerror = () => {
      releaseMic();
      setRecording(false);
      toast.error('Recording stopped unexpectedly. Please try again.');
    };
    recorder.onstop = () => {
      releaseMic();
      setRecording(false);
      const type = recorder.mimeType || mimeType || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type });
      chunksRef.current = [];
      if (cancelledRef.current) return;
      if (blob.size < 1024) {
        toast.error('That recording came through empty. Please try again, or type it.');
        return;
      }
      if (blob.size > MAX_UPLOAD_BYTES) {
        toast.error('That recording was too long to send. Please try a shorter one.');
        return;
      }
      transcribe(blob, type);
    };
    recorderRef.current = recorder;
    recorder.start(1000);
    setRecording(true);
  }, [recording, transcribing, releaseMic, transcribe]);

  const stop = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.requestData(); } catch (e) { /* not all engines allow it */ }
      recorderRef.current.stop();
    }
  }, []);
  stopRef.current = stop;

  return { canRecord: canRecordHere, recording, transcribing, elapsed, start, stop };
};

export default useVoiceNote;
